import React, { useEffect, useState } from "react";
import { api, formatAWG } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";

// Admin panel for /api/admin/service-quote-requests (Phase Services IA
// Restructure). Mirrors the SoftwareRequestsPanel visual language —
// newest-first list with expandable rows + status update dropdown.
const STATUS_OPTIONS = ["new", "contacted", "closed"];

export const ServiceQuoteRequestsPanel = () => {
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/admin/service-quote-requests");
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/service-quote-requests/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-[#A3A3A3] py-8 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading requests…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="ur-card p-10 text-center text-sm text-[#A3A3A3]">
        No service quote requests yet.
      </div>
    );
  }

  return (
    <div data-testid="service-quote-requests-panel" className="space-y-3">
      {rows.map((r) => {
        const open = expanded === r.id;
        return (
          <div
            key={r.id}
            data-testid={`service-quote-row-${r.id}`}
            className="ur-card p-5"
          >
            <button
              type="button"
              onClick={() => setExpanded(open ? null : r.id)}
              className="w-full flex items-center gap-3 text-left"
            >
              {open ? (
                <ChevronDown className="w-4 h-4 text-[#FF6600]" />
              ) : (
                <ChevronRight className="w-4 h-4 text-[#A3A3A3]" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <div className="font-display font-bold">
                    {r.service_name}
                  </div>
                  <div className="text-xs text-[#A3A3A3]">
                    {r.contact?.name || "—"} · {r.contact?.email || "—"}
                  </div>
                </div>
                <div className="text-[11px] text-[#7A7A7A] mt-1">
                  {r.selections?.length || 0} selection(s) · {(r.reference_images || []).length} reference(s) · {new Date(r.created_at).toLocaleString()}
                </div>
              </div>
              <span
                className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${
                  r.status === "new"
                    ? "bg-[#FF6600]/20 text-[#FF6600]"
                    : r.status === "contacted"
                      ? "bg-emerald-500/15 text-emerald-400"
                      : "bg-white/10 text-[#A3A3A3]"
                }`}
              >
                {r.status}
              </span>
            </button>
            {open && (
              <div className="mt-4 pt-4 border-t border-[#262626] grid md:grid-cols-2 gap-6 text-sm">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#7A7A7A]">Contact</div>
                  <div className="mt-2 space-y-1">
                    <Row label="Name" value={r.contact?.name} />
                    <Row label="Email" value={r.contact?.email} />
                    <Row label="Phone" value={r.contact?.phone} />
                    <Row label="Business" value={r.contact?.business} />
                    <Row label="Industry" value={r.contact?.industry} />
                    <Row label="Deadline" value={r.contact?.deadline} />
                  </div>
                  <div className="mt-4 text-[10px] font-bold uppercase tracking-widest text-[#7A7A7A]">Project Description</div>
                  <p className="mt-2 text-[#D1D1D1] whitespace-pre-wrap">
                    {r.contact?.description || "—"}
                  </p>
                  {r.contact?.reference_links && (
                    <>
                      <div className="mt-4 text-[10px] font-bold uppercase tracking-widest text-[#7A7A7A]">Reference Links</div>
                      <p className="mt-2 text-[#D1D1D1] whitespace-pre-wrap">
                        {r.contact.reference_links}
                      </p>
                    </>
                  )}
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#7A7A7A]">Selections</div>
                  <ul className="mt-2 space-y-1">
                    {(r.selections || []).map((s, i) => (
                      <li key={i} className="text-[#D1D1D1]">
                        <span className="text-[#7A7A7A]">{(s.kind || "item").toUpperCase()}</span> · {s.label}
                      </li>
                    ))}
                    {(r.selections || []).length === 0 && (
                      <li className="text-[#7A7A7A] italic">General estimate request</li>
                    )}
                  </ul>
                  {(r.reference_images || []).length > 0 && (
                    <>
                      <div className="mt-4 text-[10px] font-bold uppercase tracking-widest text-[#7A7A7A]">
                        Reference Uploads ({r.reference_images.length})
                      </div>
                      <ul className="mt-2 space-y-1">
                        {r.reference_images.map((u, i) => (
                          <li key={i}>
                            <a
                              href={u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[#FF6600] hover:underline text-xs break-all"
                            >
                              {u}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-xs text-[#A3A3A3]">Status:</span>
                    <select
                      data-testid={`service-quote-status-${r.id}`}
                      value={r.status}
                      onChange={(e) => updateStatus(r.id, e.target.value)}
                      className="bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[#FF6600]"
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-baseline gap-2 text-xs">
    <span className="text-[#7A7A7A] w-20 shrink-0">{label}</span>
    <span className="text-[#D1D1D1]">{value || "—"}</span>
  </div>
);
